
import React, { useEffect, useState } from 'react';
import { ApiService } from '../services/api';
import { Driver, Ticket } from '../types';
import { Search, Loader2, Phone, Mail, FileText, AlertCircle, Shield, Plus, Save, History, ExternalLink, MapPin, Zap, Lock, LayoutGrid, List, Receipt } from 'lucide-react';
import Modal from '../components/Modal';
import { useToast } from '../context/ToastContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';

type DriverViewMode = 'grid' | 'list';
const DRIVERS_VIEW_MODE_KEY = 'drivers:viewMode';

const Drivers: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAccess, promptUpgrade } = useSubscription();
  const hasRiskScoring = checkAccess('riskScoring');

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<DriverViewMode>(() => {
    try {
      const v = localStorage.getItem(DRIVERS_VIEW_MODE_KEY);
      return v === 'list' || v === 'grid' ? v : 'grid';
    } catch {
      return 'grid';
    }
  });

  // Add/Edit Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp_phone: '',
    license_number: '',
    license_expiry_date: '',
    license_issue_country: 'GB',
    license_points: 0,
    license_status: 'valid',
    address_line1: '',
    city: '',
    postcode: ''
  });

  // History Modal State
  const [viewingDriver, setViewingDriver] = useState<Driver | null>(null);
  const [driverTickets, setDriverTickets] = useState<Ticket[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    loadDrivers();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') !== '1') return;

    setIsModalOpen(true);
    params.delete('new');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : ''
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const loadDrivers = async () => {
    setLoading(true);
    const data = await ApiService.getDrivers();
    setDrivers(data || []);
    setLoading(false);
  };

  const handleSaveDriver = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await ApiService.createDriver(formData);
      addToast('Driver added successfully', 'success');
      setIsModalOpen(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        whatsapp_phone: '',
        license_number: '',
        license_expiry_date: '',
        license_issue_country: 'GB',
        license_points: 0,
        license_status: 'valid',
        address_line1: '',
        city: '',
        postcode: ''
      });
      loadDrivers();
    } catch (err) {
      addToast('Failed to add driver', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleViewHistory = async (driver: Driver) => {
    setViewingDriver(driver);
    setLoadingHistory(true);
    try {
      const { data } = await ApiService.getDriverTickets(driver.id);
      setDriverTickets(data || []);
    } catch (err) {
      addToast("Failed to load ticket history", "error");
    } finally {
      setLoadingHistory(false);
    }
  };

  const calculateRisk = (driver: Driver) => {
    let score = 0;
    const factors = [];

    const volumePoints = driver.total_pcns * 20;
    if (volumePoints > 0) {
      score += volumePoints;
      factors.push(`${driver.total_pcns} PCNs recorded (+${volumePoints})`);
    }

    if (driver.last_contravention_date) {
      const daysSince = (new Date().getTime() - new Date(driver.last_contravention_date).getTime()) / (1000 * 3600 * 24);
      if (daysSince < 30) {
        score += 25;
        factors.push('Recent contravention <30d (+25)');
      } else if (daysSince < 90) {
        score += 10;
        factors.push('Contravention in last quarter (+10)');
      }
    }

    if (driver.active_hires > 0) {
      score += 5;
      factors.push('Active driver exposure (+5)');
    }

    score = Math.min(score, 100);

    let level: 'Low' | 'Medium' | 'High' = 'Low';
    if (score >= 40) level = 'Medium';
    if (score >= 70) level = 'High';

    return { score, level, factors };
  };

  const getRiskColor = (level: string) => {
    switch(level) {
      case 'High': return 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      case 'Medium': return 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      default: return 'text-green-600 bg-green-50 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
    }
  };

  const getLicenceStatusColor = (status?: string) => {
    const normalized = String(status || '').toLowerCase();
    if (['expired', 'suspended', 'revoked'].includes(normalized)) {
      return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900';
    }
    if (normalized === 'pending_verification' || normalized === 'needs_review') {
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-900';
    }
    return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-900';
  };

  const filteredDrivers = drivers.filter(d => {
    const name = String(d.name || '').toLowerCase();
    const email = String(d.email || '').toLowerCase();
    const q = searchTerm.toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const formatAddress = (d: Driver) => {
    const parts = [d.address_line1, d.city, d.postcode].map(p => String(p || '').trim()).filter(Boolean);
    return parts.length ? parts.join(', ') : '';
  };

  const handleSetViewMode = (mode: DriverViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(DRIVERS_VIEW_MODE_KEY, mode);
    } catch {
      // ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Driver Directory</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Manage driver profiles and view AI-calculated risk scores.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1 shadow-sm">
            <button
              type="button"
              onClick={() => handleSetViewMode('grid')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'grid'
                  ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
              title="Grid view"
            >
              <LayoutGrid size={16} />
              Grid
            </button>
            <button
              type="button"
              onClick={() => handleSetViewMode('list')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'list'
                  ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
              title="List view"
            >
              <List size={16} />
              List
            </button>
          </div>
          <button 
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <Plus size={16} />
            Add Driver
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="relative w-full md:max-w-md">
           <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
           <input 
             type="text" 
             placeholder="Search drivers..." 
             className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
             value={searchTerm}
             onChange={e => setSearchTerm(e.target.value)}
           />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary-600" /></div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDrivers.map(driver => {
                const risk = calculateRisk(driver);
                const address = formatAddress(driver);
                return (
                  <div key={driver.id} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md transition-all cursor-pointer group" onClick={() => handleViewHistory(driver)}>
                     
                     {/* Header & Risk Badge */}
                     <div className="flex justify-between items-start mb-6">
                       <div className="flex items-center gap-3">
                         <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-lg font-bold text-slate-500 dark:text-slate-400 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/30 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                           {driver.name.charAt(0)}
                         </div>
                         <div>
                           <h3 className="font-bold text-slate-800 dark:text-white">{driver.name}</h3>
                           <p className="text-xs text-slate-500 dark:text-slate-400">ID: {driver.id.slice(0, 6)}</p>
                         </div>
                       </div>
                       
                       {/* Risk Badge */}
                       <div className="flex flex-col items-end">
                          {hasRiskScoring ? (
                            <>
                              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${getRiskColor(risk.level)}`}>
                                {risk.level} Risk
                              </span>
                              <span className="text-xs font-bold text-slate-400 mt-1">{risk.score}/100</span>
                            </>
                          ) : (
                             <div className="flex flex-col items-end opacity-50">
                               <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                 <Lock size={8} /> Risk Locked
                               </span>
                             </div>
                          )}
                       </div>
                     </div>
                     
                     {/* Risk Bar */}
                     <div className="mb-6 relative">
                        {!hasRiskScoring && (
                          <div 
                            className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-[2px] cursor-not-allowed"
                            onClick={(e) => { e.stopPropagation(); promptUpgrade(); }}
                          >
                             <button className="text-[10px] font-bold text-primary-600 bg-white dark:bg-slate-800 shadow-sm border dark:border-slate-700 px-2 py-1 rounded">Upgrade to View</button>
                          </div>
                        )}
                        <div className="flex justify-between text-[10px] text-slate-400 mb-1 uppercase tracking-wide font-medium">
                          <span>Risk Score</span>
                          <span>{risk.factors[0] || 'Clean Record'}</span>
                        </div>
                        <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              risk.level === 'High' ? 'bg-red-500' : 
                              risk.level === 'Medium' ? 'bg-amber-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.max(5, risk.score)}%` }}
                          ></div>
                        </div>
                     </div>

                     {/* Details */}
                     <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400 mb-6">
                       <div className="flex items-center gap-2">
                         <Mail size={14} className="text-slate-400 dark:text-slate-500" />
                         <span className="truncate">{driver.email || '—'}</span>
                       </div>
                       <div className="flex items-center gap-2">
                         <Phone size={14} className="text-slate-400 dark:text-slate-500" />
                         <span className="truncate">{driver.phone || '—'}</span>
                       </div>
                       <div className="flex items-center gap-2">
                         <Shield size={14} className="text-slate-400 dark:text-slate-500" />
                         <span className="truncate">{driver.license_number || '—'}</span>
                       </div>
                       <div className="flex items-center gap-2 flex-wrap">
                         <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${getLicenceStatusColor(driver.license_status)}`}>
                           {driver.license_status || 'unknown'}
                         </span>
                         {driver.license_expiry_date && (
                           <span className="text-xs text-slate-500 dark:text-slate-400">
                             Exp {new Date(driver.license_expiry_date).toLocaleDateString('en-GB')}
                           </span>
                         )}
                         {typeof driver.license_points === 'number' && (
                           <span className="text-xs text-slate-500 dark:text-slate-400">
                             Points: {driver.license_points}
                           </span>
                         )}
                       </div>
                       <div className="flex items-center gap-2">
                         <MapPin size={14} className="text-slate-400 dark:text-slate-500" />
                         <span className="truncate">{address || '—'}</span>
                       </div>
                     </div>

                     {/* Stats Footer */}
                     <div className="grid grid-cols-2 gap-4 border-t border-slate-100 dark:border-slate-800 pt-4 bg-slate-50/50 dark:bg-slate-800/30 -mx-6 -mb-6 px-6 pb-4 mt-auto rounded-b-xl">
                       <div className="text-center">
                         <p className="text-2xl font-bold text-slate-800 dark:text-white">{driver.total_pcns}</p>
                         <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide font-semibold">Total PCNs</p>
                       </div>
                       <div className="text-center border-l border-slate-200 dark:border-slate-800">
                         <p className="text-2xl font-bold text-slate-800 dark:text-white">{driver.active_hires}</p>
                         <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide font-semibold">Active Hires</p>
                       </div>
                     </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-950/40 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Driver</th>
                      <th className="text-left px-4 py-3 font-semibold">Contact</th>
                      <th className="text-left px-4 py-3 font-semibold">Address</th>
                      <th className="text-left px-4 py-3 font-semibold">Licence</th>
                      <th className="text-left px-4 py-3 font-semibold">PCNs</th>
                      <th className="text-left px-4 py-3 font-semibold">Active</th>
                      <th className="text-left px-4 py-3 font-semibold">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrivers.map(driver => {
                      const risk = calculateRisk(driver);
                      const address = formatAddress(driver);
                      return (
                        <tr
                          key={driver.id}
                          className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
                          onClick={() => handleViewHistory(driver)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-[180px]">
                              <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-sm font-bold text-slate-500 dark:text-slate-400">
                                {driver.name.charAt(0)}
                              </div>
                              <div className="min-w-0">
                                <div className="font-bold text-slate-800 dark:text-white truncate">{driver.name}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">ID: {driver.id.slice(0, 6)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            <div className="flex flex-col gap-1 min-w-[220px]">
                              <div className="flex items-center gap-2">
                                <Mail size={14} className="text-slate-400 dark:text-slate-500" />
                                <span className="truncate">{driver.email || '—'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone size={14} className="text-slate-400 dark:text-slate-500" />
                                <span className="truncate">{driver.phone || '—'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            <div className="flex items-center gap-2 min-w-[240px]">
                              <MapPin size={14} className="text-slate-400 dark:text-slate-500" />
                              <span className="truncate">{address || '—'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1 min-w-[180px]">
                              <span className={`inline-flex w-fit text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${getLicenceStatusColor(driver.license_status)}`}>
                                {driver.license_status || 'unknown'}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {driver.license_expiry_date
                                  ? `Exp ${new Date(driver.license_expiry_date).toLocaleDateString('en-GB')}`
                                  : 'No expiry'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-bold text-slate-800 dark:text-white">{driver.total_pcns}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-bold text-slate-800 dark:text-white">{driver.active_hires}</span>
                          </td>
                          <td className="px-4 py-3">
                            {hasRiskScoring ? (
                              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${getRiskColor(risk.level)}`}>
                                {risk.level} ({risk.score})
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 inline-flex items-center gap-1"
                                onClick={(e) => { e.stopPropagation(); promptUpgrade(); }}
                                title="Upgrade to view risk"
                              >
                                <Lock size={8} /> Locked
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Driver Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Add New Driver"
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleSaveDriver} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full Name *</label>
            <input 
              required
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              placeholder="e.g. John Doe"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
             <div>
               <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
               <input 
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  placeholder="john@example.com"
               />
             </div>
             <div>
               <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone</label>
               <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  placeholder="07700 900000"
               />
             </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              WhatsApp Phone
              <span className="text-slate-500 dark:text-slate-400 font-normal ml-2">(optional)</span>
            </label>
            <input
               type="tel"
               value={formData.whatsapp_phone}
               onChange={e => setFormData({...formData, whatsapp_phone: e.target.value})}
               className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
               placeholder="+447700900000"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              For invoice notifications via WhatsApp. Use international format (e.g., +447700900000).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">License Number</label>
            <input 
              type="text"
              value={formData.license_number}
              onChange={e => setFormData({...formData, license_number: e.target.value})}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none uppercase font-mono bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              placeholder="SMITH123456AB78"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Licence Expiry</label>
              <input
                type="date"
                value={formData.license_expiry_date}
                onChange={e => setFormData({ ...formData, license_expiry_date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Issue Country</label>
              <input
                type="text"
                value={formData.license_issue_country}
                onChange={e => setFormData({ ...formData, license_issue_country: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none uppercase bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                placeholder="GB"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Licence Points</label>
              <input
                type="number"
                min={0}
                value={formData.license_points}
                onChange={e => setFormData({ ...formData, license_points: Number(e.target.value || 0) })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Licence Status</label>
              <select
                value={formData.license_status}
                onChange={e => setFormData({ ...formData, license_status: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              >
                <option value="valid">Valid</option>
                <option value="pending_verification">Pending verification</option>
                <option value="expired">Expired</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <MapPin size={16} className="text-slate-400" /> Address Details
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Address Line 1</label>
                <input 
                  type="text"
                  value={formData.address_line1}
                  onChange={e => setFormData({...formData, address_line1: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  placeholder="123 High Street"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">City</label>
                  <input 
                    type="text"
                    value={formData.city}
                    onChange={e => setFormData({...formData, city: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    placeholder="London"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Postcode</label>
                  <input 
                    type="text"
                    value={formData.postcode}
                    onChange={e => setFormData({...formData, postcode: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none uppercase bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    placeholder="SW1A 1AA"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 mt-4">
             <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm">Cancel</button>
             <button 
               type="submit" 
               disabled={saving}
               className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 flex items-center gap-2"
             >
               {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
               Save Driver
             </button>
          </div>
        </form>
      </Modal>

      {/* Driver History Modal */}
      <Modal
        isOpen={!!viewingDriver}
        onClose={() => setViewingDriver(null)}
        title={viewingDriver ? `${viewingDriver.name} - Risk Analysis` : 'History'}
        maxWidth="max-w-2xl"
      >
        {viewingDriver && (
          <div className="p-6">
            
            {/* Risk Breakdown */}
            <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700">
               <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
                 <AlertCircle size={16} className="text-primary-600 dark:text-primary-400" /> Risk Factors
               </h4>
               <ul className="space-y-2">
                 {calculateRisk(viewingDriver).factors.map((factor, i) => (
                   <li key={i} className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
                     <Zap size={14} className="text-amber-500" />
                     {factor}
                   </li>
                 ))}
                 {calculateRisk(viewingDriver).factors.length === 0 && (
                   <li className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
                     <Shield size={14} /> Clean Record - Low Risk
                   </li>
                 )}
               </ul>
            </div>

            <div className="flex items-center gap-4 mb-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-lg">
               <div className="flex-1">
                 <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Calculated Score</p>
                 <span className={`text-sm font-bold px-2 py-0.5 rounded border inline-block mt-1 ${getRiskColor(calculateRisk(viewingDriver).level)}`}>
                    {calculateRisk(viewingDriver).level} ({calculateRisk(viewingDriver).score}/100)
                 </span>
               </div>
               <div className="flex-1 border-l border-slate-200 dark:border-slate-700 pl-4">
                 <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Liability</p>
                 <p className="font-bold text-slate-800 dark:text-white">£{driverTickets.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}</p>
               </div>
               <div className="flex-1 border-l border-slate-200 dark:border-slate-700 pl-4">
                 <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Count</p>
                 <p className="font-bold text-slate-800 dark:text-white">{driverTickets.length} Tickets</p>
               </div>
            </div>

            <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
              <History size={16} className="text-primary-600 dark:text-primary-400" />
              PCN Timeline
            </h3>
            
            {loadingHistory ? (
               <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
            ) : driverTickets.length > 0 ? (
              <div className="space-y-3">
                {driverTickets.map(ticket => (
                  <div key={ticket.id} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded">
                        <FileText size={18} />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 dark:text-white text-sm">{ticket.pcn_number}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(ticket.issue_date).toLocaleDateString('en-GB')} • {ticket.vrn}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">£{ticket.amount}</span>
                      <button 
                        onClick={() => {
                          setViewingDriver(null); // Close modal
                          navigate(`/tickets/${ticket.id}`);
                        }}
                        className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-400 hover:text-primary-600"
                      >
                        <ExternalLink size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400 dark:text-slate-500 border border-dashed border-slate-200 dark:border-slate-800 rounded-lg">
                No tickets found for this driver.
              </div>
            )}
            
            <div className="flex justify-between mt-6">
              <button
                onClick={() => {
                  setViewingDriver(null);
                  navigate('/driver-invoices');
                }}
                className="px-4 py-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Receipt size={16} />
                View Invoices
              </button>
              <button
                onClick={() => setViewingDriver(null)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Drivers;
